import React from "react";
import { useProductCardActions } from "../../../../../providers/useProductActions";
import { useCartData } from "../../../../../providers/cartDataStore";
import { Product, ImageType, ProductImageTypes, ProductType } from "@core/types";
import { Image } from "../../../../../components/Image/Image";
import { IonButton, IonIcon } from "@ionic/react";
import { useHistory, useLocation } from "react-router-dom";
import { useProductSubscriptionsStore } from "../../../../../providers/products";
import useAddProductSubscription from "../../../../../hooks/useAddProductSubscription";
import { heart } from "ionicons/icons";
import { useWishlistToggle } from "../../../../../providers/wishlist";
import { useTranslation } from "react-i18next";
import cln from "classnames";
import useOpenProductDetailSheet from "../../../../../hooks/useOpenProductDetailSheet";
import { QuantityInput } from "../QuantityInput";
import { useConfig } from "../../../../../providers/config";
import { getProductQuantityStep } from "../../../../../utils/productQuantity";
import { prettifyNumbers } from "../../../../../utils/prettifyNumbers";
interface ProductCartProps {
  product: Product;
  lazyload?: boolean;
  showCounter?: boolean;
}

const MainPageProduct = ({ product, lazyload, showCounter = true }: ProductCartProps) => {
  const { t } = useTranslation();
  const config = useConfig();
  const openProductDetailSheet = useOpenProductDetailSheet();
  const productImage = { ...product?.images[0], type: ProductImageTypes.img } as ImageType;
  const { addToCart, removeProductFromCart } = useProductCardActions();
  const { cartProductsMap } = useCartData();
  const cartQuantity = cartProductsMap[product.id]?.quantity ?? 0;
  const step = getProductQuantityStep({
    product,
  });
  const isDisabled =
    (product.stockQuantity > cartQuantity && product.stockQuantity - cartQuantity < step) ||
    product.stockQuantity === cartQuantity;
  const { toggleWish, isWishProduct } = useWishlistToggle();

  const { productSubscriptionIdsMap } = useProductSubscriptionsStore.getState();
  const productSubscription = productSubscriptionIdsMap[product.id];

  const handleOpenProductDetail = () => {
    openProductDetailSheet({ id: product.id, productType: product.type });
  };

  const handleAddToCart = () => {
    if (product.type === ProductType.VARIABLE) {
      handleOpenProductDetail();
      return;
    }

    addToCart(product);
  };

  const { handleAddProductSubscription, isLoading } = useAddProductSubscription(product.id);

  const isDiscount =
    product.discountPrice &&
    product.discountPrice !== "0" &&
    product.discountPrice !== "0.00" &&
    product.discountPrice !== product.price;

  return (
    <div className={cln("flex flex-col items-center justify-between gap-y-3 w-28 h-[11.5rem] bg-white relative")}>
      <div onClick={handleOpenProductDetail} className="cursor-pointer">
        <div className="">
          <div
            className="flex items-center absolute top-0 right-0 bg-gray-100 rounded-full p-1.5"
            onClick={(e) => {
              e.stopPropagation();
              toggleWish(product?.id!);
            }}
          >
            <IonIcon
              className={`text-[0.875rem] ${isWishProduct(product?.id!) ? "text-gray-800" : "text-gray-300"}`}
              icon={heart}
            />
          </div>
        </div>
        <Image
          lazyLoad={false}
          draggable={false}
          image={productImage}
          placeholder={productImage.base64}
          alt={product.name}
          className="w-20 h-20 mb-3"
        />
        <div className="text-center">
          <p className="text-[8px] font-thin text-gray-600">
            до {product.stockQuantity} {t(product.portionUnit!)}
          </p>
          {isDiscount && (
            <p className="text-xs font-bold text-[#FF1E1E]">
              {Number(product.discountPrice)} ₽/{t(product.portionUnit!)}
            </p>
          )}

          <p
            className={cln("text-xs font-bold text-gray-800", {
              "line-through text-[0.5rem] !text-[#838383]": isDiscount,
            })}
          >
            {Number(product.price)} ₽/{t(product.portionUnit!)}
          </p>
        </div>
      </div>
      <div className="flex flex-col items-center justify-between gap-y-2 w-full">
        {product.stockQuantity > 0 ? (
          cartQuantity > 0 ? (
            <div className="flex items-center justify-center text-xs w-full h-9 border border-primary rounded-lg">
              <IonButton
                fill="clear"
                type="button"
                size="small"
                onClick={() => removeProductFromCart(product.id)}
                className="text-primary"
              >
                <p>-</p>
              </IonButton>
              {config.isCartQuantityInputEnabled ? (
                <QuantityInput
                  product={product}
                  cartQuantity={cartQuantity}
                  className="w-9 h-6 text-center bg-gray-100 rounded-md outline-none mx-0.5"
                />
              ) : (
                <span className="font-bold mx-2 px-1.5 py-0.5 bg-gray-100 rounded-sm">
                  {prettifyNumbers(cartQuantity)}
                </span>
              )}
              <IonButton
                fill="clear"
                type="button"
                size="small"
                onClick={handleAddToCart}
                disabled={isDisabled}
                className={`${isDisabled ? "text-gray-500" : "text-primary"}`}
              >
                <p>+</p>
              </IonButton>
            </div>
          ) : (
            <button
              onClick={handleAddToCart}
              type="button"
              className=" bg-primary text-xs w-full rounded-lg m-0 py-2.5"
            >
              {t("Add to cart")}
            </button>
          )
        ) : (
          <button
            disabled={!!productSubscription || isLoading}
            type="button"
            onClick={handleAddProductSubscription}
            className={`w-full text-xs rounded-lg py-2.5 font-semibold text-white bg-[#CDCDCD] ${
              !!productSubscription || isLoading ? "opacity-50 cursor-not-allowed" : ""
            }`}
          >
            {productSubscription ? t("Coming soon") : t("Notify")}
          </button>
        )}
      </div>
    </div>
  );
};

export default MainPageProduct;
